import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { tokens } from '@news-mono/design-tokens'
import { TNToastComponentProps } from '../../../../content'
import { TNToastComponent } from '../../../../content/TNToastNotification/TNToastNotification'
import { StyledCTAWardenButton, Heading } from './component.styled'
import {
    StyledLayout,
    StyledInput,
    StyledInputWrapper,
    StyledValidationError,
    StyledColumnA,
    StyledColumnB,
    StyledBodyText,
    StyledForm,
    StyledButtonWrapper,
    StyledSpinner,
} from './SaveNameContent.styled'
import { useChangeName } from '../../../../user-setting/hooks/useChangeName'
import { UserSettingProps } from '../../../../user-setting'
import { authCheckAccessToken } from '@news-mono/web-common'

interface VerifyEmailContentProps extends UserSettingProps {
    auth0Id: string
}

interface FormData {
    firstName: string
    lastName: string
}

interface ValidationErrors {
    firstName?: string
    lastName?: string
}

export const SaveNameContent = ({
    auth0Id,
    onEvent,
    log,
    config,
}: VerifyEmailContentProps) => {
    const dispatch = useDispatch()

    const [statusBanner, setStatusBanner] =
        useState<TNToastComponentProps | null>(null)

    const [isRefreshingAuth, setIsRefreshingAuth] = useState<boolean>(false)

    const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
        {},
    )

    const [formData, setFormData] = useState<FormData>({
        firstName: '',
        lastName: '',
    })

    const { mutateAsync: changeName, isLoading: isSaving } = useChangeName({
        firstName: formData.firstName,
        lastName: formData.lastName,
        userId: auth0Id,
        onSuccess: () => {
            setIsRefreshingAuth(true)

            dispatch(
                authCheckAccessToken({
                    onEvent,
                    log,
                    config,
                    invocation: 'auto',
                    forceRefresh: true,
                }),
            )
            setInterval(() => {
                // Just incase something goes wrong with the auth refresh
                setIsRefreshingAuth(false)
            }, 5000)
        },
        onError: () => {
            setStatusBanner({
                type: 'error',
                text: 'Something went wrong, please try again.',
                useMaxWidth: false,
                forceMaxWidth: true,
            })
        },
    })

    const isBusy = isSaving || isRefreshingAuth

    const validate = (): boolean => {
        const errors: ValidationErrors = {}

        const firstName = formData.firstName.trim()

        if (firstName === '') {
            errors.firstName = 'Please enter a first name.'
        } else if (formData.firstName.trim().length > 50) {
            errors.firstName = 'First name should be less than 50 characters.'
        }

        const lastName = formData.lastName.trim()

        if (lastName === '') {
            errors.lastName = 'Please enter a last name.'
        } else if (formData.lastName.trim().length > 50) {
            errors.lastName = 'Last name should be less than 50 characters.'
        }

        setValidationErrors(errors)
        return !Object.keys(errors).length
    }

    const onSave = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        setStatusBanner(null)

        if (!validate()) {
            return
        }
        changeName()
    }

    return (
        <>
            {statusBanner && <TNToastComponent {...statusBanner} />}
            <StyledLayout>
                <StyledColumnA>
                    <Heading>Want to leave a comment?</Heading>
                    <StyledBodyText>
                        Finish your profile by entering your first and last
                        name. You can change this later in your Account.
                    </StyledBodyText>
                </StyledColumnA>

                <StyledColumnB>
                    <StyledForm method="POST" onSubmit={onSave}>
                        <StyledInputWrapper>
                            <StyledInput
                                name="first-name"
                                disabled={isBusy}
                                id="savename-first-name"
                                value={formData.firstName}
                                type="name"
                                placeholder="First name"
                                hasError={!!validationErrors?.firstName}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>,
                                ) =>
                                    setFormData({
                                        ...formData,
                                        firstName: e.currentTarget.value,
                                    })
                                }
                            />
                        </StyledInputWrapper>
                        {validationErrors?.firstName && (
                            <StyledValidationError>
                                {validationErrors.firstName}
                            </StyledValidationError>
                        )}
                        <StyledInputWrapper>
                            <StyledInput
                                name="last-name"
                                disabled={isBusy}
                                id="savename-last-name"
                                value={formData.lastName}
                                type="name"
                                placeholder="Last name"
                                hasError={!!validationErrors?.lastName}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>,
                                ) =>
                                    setFormData({
                                        ...formData,
                                        lastName: e.currentTarget.value,
                                    })
                                }
                            />
                        </StyledInputWrapper>
                        {validationErrors?.lastName && (
                            <StyledValidationError>
                                {validationErrors.lastName}
                            </StyledValidationError>
                        )}

                        <StyledButtonWrapper>
                            <StyledCTAWardenButton
                                type="submit"
                                disabled={isBusy}
                                backgroundColors={{
                                    default:
                                        tokens.thenightly.colors.palette.utility
                                            .error.default,
                                    hover: tokens.thenightly.colors.palette
                                        .utility.error.hover,
                                }}
                                roundEdges={true}
                                fullWidthButton={true}
                            >
                                {isBusy ? <StyledSpinner /> : 'SAVE'}
                            </StyledCTAWardenButton>
                        </StyledButtonWrapper>
                    </StyledForm>
                </StyledColumnB>
            </StyledLayout>
        </>
    )
}
